import { isIos } from '@umu/umu-ua';
import { $window, $document } from '@umu/umu-dom';
import { getCaretCoordinates } from './position';
import { calcIosHeight } from './calcIosHeight';

const headerHeight = 54;
const toolbarHeight = 69;

interface IPosition {
    height: number;
    left: number;
    top: number;
}

export class AutosizeController {
    public contentHeight = 0;
    private el: HTMLTextAreaElement | null = null;
    private container: HTMLElement | null = null;
    private maxHeight: number = 0;
    private heightOffset: number = 0;
    private isThrottle = false;
    private currentClientHeight = 0;
    private keyboardHeight = 0;
    // private currentCursorTop = 0;  // 当前光标的位置

    constructor(el: HTMLTextAreaElement, scrollContainer: HTMLElement | null) {
        this.el = el;
        this.container = scrollContainer;
        this.bindEvent();
    }

    public init = () => {
        if (
            !this.el ||
            !this.el.nodeName ||
            this.el.nodeName !== 'TEXTAREA' ||
            this.el.hasAttribute('data-autosize-on')
        ) {
            return;
        }

        const style = $window.getComputedStyle(this.el, null);

        if (style.resize === 'vertical') {
            this.el.style.resize = 'none';
        } else if (style.resize === 'both') {
            this.el.style.resize = 'horizontal';
        }

        // horizontal overflow is hidden, so break-word is necessary for handling words longer than the textarea width
        this.el.style.wordWrap = 'break-word';

        // Chrome/Safari-specific fix:
        // When the textarea y-overflow is hidden, Chrome/Safari doesn't reflow the text to account for the space
        // made available by removing the scrollbar. This workaround will cause the text to reflow.
        const width = this.el.style.width;
        this.el.style.width = '0px';
        // Force reflow:
        // tslint:disable-next-line:no-unused-expression
        this.el.offsetWidth;
        this.el.style.width = width;

        this.maxHeight = style.maxHeight !== 'none' ? parseFloat(style.maxHeight || '0') : 0;

        if (style.boxSizing === 'content-box') {
            this.heightOffset = -(parseFloat(style.paddingTop || '0') + parseFloat(style.paddingBottom || '0'));
        } else {
            this.heightOffset = parseFloat(style.borderTopWidth || '0') + parseFloat(style.borderBottomWidth || '0');
        }

        this.el.setAttribute('data-autosize-on', 'true');
        this.el.style.overflow = 'hidden';
        this.el.style.overflowY = 'hidden';

        this.adjust();
    };

    public destory = () => {
        this.unbindEvent();
    };

    private adjust = () => {
        if (!this.el) {
            return;
        }

        if (!this.container) {
            return;
        }

        const startHeight = this.el.style.height;
        const scrollTop = this.container.scrollTop;
        this.el.style.height = 'auto';
        let endHeight = this.el.scrollHeight + this.heightOffset;

        const style = $window.getComputedStyle(this.el, null);
        this.maxHeight = style.maxHeight !== 'none' ? parseFloat(style.maxHeight || '0') : 0;

        if (this.maxHeight !== 0 && this.maxHeight < endHeight) {
            endHeight = Number(this.maxHeight);
            if (this.el.style.overflowY !== 'scroll') {
                this.el.style.overflowY = 'scroll';
            }
        } else if (this.el.style.overflowY !== 'hidden') {
            this.el.style.overflowY = 'hidden';
        }

        this.el.style.height = endHeight + 'px';
        this.contentHeight = endHeight;

        // prevents scroll-position jumping
        this.container.scrollTop = scrollTop;

        if (startHeight !== this.el.style.height) {
            const evt = $document.createEvent('Event');
            evt.initEvent('autosize.resized', true, false);
            this.el.dispatchEvent(evt);
        }
        this.setScrollTopPosition(scrollTop);
    };

    private setScrollTopPosition = (scrollTop: number) => {
        // 简易节流
        if (!this.isThrottle) {
            this.isThrottle = true;
            $window.setTimeout(() => {
                if (!this.el) {
                    return;
                }
                if (!this.container) {
                    return;
                }
                const p: IPosition = getCaretCoordinates(this.el, this.el.selectionEnd);

                if (!this.isInView(p, scrollTop)) {
                    // todo
                    // 解决复制文本时，没有滚动到光标处
                    // this.container.scrollTop = p.top - $window.innerHeight - this.keyboardHeight + (3 * 17 + 140);
                    // 解决输入时光标可见的问题
                    this.container.scrollTop = scrollTop + 3 * 17;
                }

                this.isThrottle = false;
            }, 100);
        }
    };

    private isInView = (p: IPosition, scrollTop: number) => {
        if (!this.container) {
            return false;
        }
        const { top } = p;
        const { offsetHeight } = this.container;
        const viewH = offsetHeight - headerHeight;
        return top + 2 * 17 - scrollTop < viewH - toolbarHeight;
    };

    private setPosition = () => {
        $window.setTimeout(() => {
            if (!this.el) {
                return;
            }
            if (!this.container) {
                return;
            }
            const p: IPosition = getCaretCoordinates(this.el, this.el.selectionEnd);
            if (!this.isInView(p, this.container.scrollTop)) {
                const scrollTop = p.top - this.keyboardHeight + (3 * 17 + 140);
                this.container.scrollTop = scrollTop;
            }
            this.isThrottle = false;
        }, 100);
    };

    private onFocusin = () => {
        if (isIos) {
            this.keyboardHeight = calcIosHeight();
        }
        this.init();
        this.currentClientHeight = $window.innerHeight;

        this.setPosition();
    };

    private onFocusout = () => {
        if (isIos) {
            this.keyboardHeight = 0;
        }
        this.init();
    };

    public onResize = () => {
        const nowClientHeight = $window.innerHeight;
        if (this.currentClientHeight > nowClientHeight) {
            // 键盘弹出的事件处理
            this.keyboardHeight = this.currentClientHeight - nowClientHeight;
            this.setPosition();
        } else {
            // 键盘收起的事件处理
            this.keyboardHeight = 0;
        }
        this.init();
        const wrap = $document.querySelector('.topic-editor-wrap') as HTMLElement;
        if (wrap) {
            if (this.keyboardHeight > 0) {
                wrap.style.paddingBottom = `${this.keyboardHeight}px`;
            } else {
                wrap.style.paddingBottom = '0';
            }
            console.log(`this.keyboardHeight: ${this.keyboardHeight}`);
        }
        this.adjust();
        this.currentClientHeight = $window.innerHeight;
    };

    private bindEvent = () => {
        if (!this.el) {
            return;
        }
        // IE9 does not fire onpropertychange or oninput for deletions,
        // so binding to onkeyup to catch most of those events.
        // There is no way that I know of to detect something like 'cut' in IE9.
        if ('onpropertychange' in this.el && 'oninput' in this.el) {
            (this.el as any).addEventListener('keyup', this.adjust);
        }

        $window.addEventListener('resize', this.onResize);
        this.el.addEventListener('input', this.adjust);
        this.el.addEventListener('focusin', this.onFocusin, false);
        this.el.addEventListener('focusout', this.onFocusout, false);
    };

    private unbindEvent = () => {
        if (!this.el) {
            return;
        }
        if ('onpropertychange' in this.el && 'oninput' in this.el) {
            (this.el as any).removeEventListener('keyup', this.adjust);
        }
        $window.removeEventListener('resize', this.onResize);
        this.el.removeEventListener('input', this.adjust);
        this.el.removeEventListener('focusin', this.onFocusin, false);
        this.el.removeEventListener('focusout', this.onFocusout, false);
    };
}
